import React, { useState, useEffect } from "react";
import { BellIcon } from "@heroicons/react/24/solid";
import { PiHandWaving } from "react-icons/pi";
import axios from "axios";

const Header = ({ user, selectedService, setSelectedService }) => {
  const today = new Date();
  const [notifications, setNotifications] = useState(["Test notification!"]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [callStats, setCallStats] = useState({
    callLimit: null,
    callsUsed: 0,
    remainingCalls: null,
    hasUnlimitedCalls: true,
    minuteLimitPerCall: null,
    totalMinuteLimit: null,
    minutesUsed: 0,
    remainingMinutes: null,
    hasUnlimitedMinutes: true
  });

  const formattedDate = today.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Dynamic greeting based on time of day
  const hour = new Date().getHours();
  const getGreeting = () => {
    if (hour >= 21 || hour < 6) return "Good Night";
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const handleClearNotifications = () => {
    setNotifications([]);
    setShowDropdown(false);
  };

  // Fetch call statistics
  const fetchCallStats = async () => {
    if (!user) return;

    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser || !storedUser.id) return;

    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/users/call-stats/${storedUser.id}`);
      setCallStats(response.data);
    } catch (error) {
      console.error('Error fetching call stats:', error);
    }
  };

  // Fetch call stats on mount and set up polling
  useEffect(() => {
    fetchCallStats();

    // Poll every 30 seconds for updates
    const interval = setInterval(fetchCallStats, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="flex justify-between items-center p-4 border-b shadow-md bg-gradient-to-r from-gray-900 via-gray-450 to-gray-300">
      {/* Left: Greeting and Date */}
      <div className="flex items-center space-x-4">
        <div
          className="flex items-center space-x-2"
          style={{
            animation: "fadeIn 1s ease-in-out"
          }}
        >
          <PiHandWaving className="h-7 w-7 text-yellow-500 wave-animation" />
          <span className="text-lg font-bold text-gray-700">
            <span className="text-blue-600">{getGreeting()}, {user || "User"}!</span>
          </span>
        </div>
        <span className="text-white text-sm">{formattedDate}</span>
      </div>

      {/* Center: Service Selector */}
      <div className="flex items-center space-x-2 bg-gray-700 px-4 py-2 rounded-lg shadow-inner">
        <span className="text-white text-sm font-medium">Service:</span>
        <select
          value={selectedService}
          onChange={(e) => setSelectedService(e.target.value)}
          className="bg-gray-800 text-white text-sm font-bold rounded border-none focus:ring-2 focus:ring-blue-500 cursor-pointer p-1"
        >
          <option value="Outbound">Outbound</option>
          <option value="Inbound">Inbound</option>
        </select>
      </div>

      {/* Center Right: Remaining Calls & Minutes Display */}
      <div className="flex items-center gap-1 bg-gray-800/80 backdrop-blur-md px-1 py-1 rounded-2xl shadow-xl border border-white/10">
        <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl border border-blue-500/20">
          <div className="flex flex-col">
            <span className="text-[10px] text-blue-400 font-bold uppercase tracking-widest leading-none mb-1">Calls Left</span>
            <span className="text-2xl font-black text-white leading-none">
              {callStats.hasUnlimitedCalls ? '∞' : callStats.remainingCalls}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 rounded-xl border border-emerald-500/20">
          <div className="flex flex-col">
            <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest leading-none mb-1">Mins Left</span>
            <span className="text-2xl font-black text-white leading-none">
              {callStats.hasUnlimitedMinutes ? '∞' : callStats.remainingMinutes}
            </span>
          </div>
        </div>

        {callStats.minuteLimitPerCall && (
          <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-br from-amber-500/10 to-amber-600/10 rounded-xl border border-amber-500/20">
            <div className="flex flex-col">
              <span className="text-[10px] text-amber-400 font-bold uppercase tracking-widest leading-none mb-1">Per Call</span>
              <span className="text-2xl font-black text-white leading-none font-mono">
                {callStats.minuteLimitPerCall}m
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Right: Notification Bell */}
      <div className="relative text-gray-500">
        <div
          className="relative cursor-pointer hover:text-blue-600 transition duration-300"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <BellIcon className="h-6 w-6" />
          {notifications.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full px-1">
              {notifications.length}
            </span>
          )}
        </div>

        {/* Notification Dropdown */}
        {showDropdown && (
          <div className="absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-lg p-4 z-10">
            {notifications.length > 0 ? (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-bold text-gray-700">Notifications</span>
                  <button
                    className="text-xs text-blue-500 hover:underline"
                    onClick={handleClearNotifications}
                  >
                    Clear All
                  </button>
                </div>
                <ul className="space-y-2">
                  {notifications.map((notification, index) => (
                    <li
                      key={index}
                      className="text-sm text-gray-600 bg-gray-100 p-2 rounded-md hover:bg-gray-200 transition"
                    >
                      {notification}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No new notifications</div>
            )}
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes wave {
          0% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(20deg);
          }
          50% {
            transform: rotate(0deg);
          }
          75% {
            transform: rotate(-20deg);
          }
          100% {
            transform: rotate(0deg);
          }
        }

        .wave-animation {
          animation: wave 1.5s infinite;
          transform-origin: 50% 50%; /* Adjusts the pivot point for rotation */
        }
      `}</style>
    </div>
  );
};

export default Header;
